import * as gs from "@ivanlog/gs";
import * as com from "./tmpls";
import { LPXInfo } from "../api/classes/models";
import { App } from "../App";
import { LPXInfrastructure } from "../contracts/LPXInfrastructure";
import { BiMath } from "../libs/BiMath";
import { ERC20Contract } from "../contracts/ERC20Contract";
import { LPXReserves } from "../contracts/LPXContract";

type StartPoolStatusState = ""|"warning"|"invalid";
type StartPoolPriceRatio = { numerator:bigint; denominator:bigint };
type StartPoolCheckInput = {
    poolId:number;
    fundAddress:string;
    anchorAddress:string;
    fundReserve:bigint;
    anchorReserve:bigint;
    sellPriceRaw:bigint;
    maxSellPriceRaw:bigint;
    sellPriceExact:bigint;
    maxSellPriceExact:bigint;
    sellPriceExactRatio:StartPoolPriceRatio;
    feeIndex:bigint;
    ntzIndex:bigint;
    key:string;
};
type StartPoolCheckResult = ReturnType<LPXInfrastructure["check"]>;
type StartPoolCheckCache = {
    key:string;
    result:StartPoolCheckResult;
};
type StartPoolValidation =
    { state:"empty" } |
    { state:"invalid"; statusText:string; dialogText:string } |
    { state:"valid"; input:StartPoolCheckInput; result:StartPoolCheckResult };

export class CmpWidgetLPXCtrlTabStartPool extends com.CmpWidgetLPXCtrlTabStartPoolBase {

    private static readonly VALIDATION_DEBOUNCE_MS = 250;

    private _pool:LPXInfo;
    private _ntz:number;
    private _lpxInf:LPXInfrastructure;
    private _fundDecimal:bigint = BiMath.Decimal;
    private _anchorDecimal:bigint = BiMath.Decimal;
    private _status:gs.WrapperGS|undefined;
    private _validationTimer:number|undefined;
    private _checkCache:StartPoolCheckCache|undefined;
    private _reserveStatusText:string = "";
    private _isStarting = false;
    constructor() {
        super();

        const ctrl = this.ctrls;
        this._status = this.q(".start-pool-status").first;
        if (this._status)
            this._status.onclick = ()=> {
                if (!this._reserveStatusText) return;
                App.gui.dialogOkShowAsync(this._reserveStatusText, "Published reserves");
            };
        ctrl.cmpChart.ctrls.lTitle.hide();
        ctrl.btnStartLPX.disable();

        ctrl.btnStartLPX.onclick=async ()=>{
            if (this._isStarting) return;
            this._isStarting = true;
            try
            {
                this._clearStartValidationTimer();
                const validation = this._getStartValidation();
                if (validation.state == "empty")
                {
                    ctrl.btnStartLPX.disable();
                    ctrl.cmpChart.setLinePrice("minBuy", 0);
                    this._setStartStatus("");
                    return;
                }
                if (validation.state == "invalid")
                {
                    ctrl.btnStartLPX.disable();
                    ctrl.cmpChart.setLinePrice("minBuy", 0);
                    this._setStartStatus(validation.statusText, "invalid");
                    await App.gui.dialogOkShowAsync(validation.dialogText);
                    return;
                }

                const publishedPrices = this._getPublishedPrices(validation.result.reserves, validation.input.feeIndex);
                let startInput = validation.input;
                let startResult = validation.result;
                if (publishedPrices.sellPrice != validation.input.sellPriceExact || publishedPrices.maxSellPrice != validation.input.maxSellPriceExact)
                {
                    const confirm = await App.gui.dialogOkNoShowAsync(
                        "The prices that will be published differ from your input.\n\n"+
                        "Input Sell Price: "+BiMath.ToEth(validation.input.sellPriceExact)+"\n"+
                        "Published Sell Price: "+BiMath.ToEth(publishedPrices.sellPrice)+"\n\n"+
                        "Input Max Sell Price: "+BiMath.ToEth(validation.input.maxSellPriceExact)+"\n"+
                        "Published Max Sell Price: "+BiMath.ToEth(publishedPrices.maxSellPrice)+"\n\n"+
                        "Start LPX with the published prices?",
                        "Review prices"
                    );
                    if (!confirm) return;

                    const currentValidation = this._getStartValidation();
                    if (currentValidation.state != "valid" || currentValidation.input.key != validation.input.key)
                    {
                        this._refreshStartValidation();
                        await App.gui.dialogOkShowAsync("Parameters changed while confirmation was open. Review the published prices and click Start again.");
                        return;
                    }
                    startInput = currentValidation.input;
                    startResult = currentValidation.result;
                }

                await App.gui.awaitInLockUIAsync(Promise.resolve().then(()=>this._tryStartLPXAsync(startInput, startResult)));
            }
            finally
            {
                this._isStarting = false;
            }
        };

        ctrl.cmpStartMaxPrice.ctrls.iValue.el.addEventListener("input",()=>{
            this._refreshStartPriceLines();
            this._scheduleStartValidation();
        });
        ctrl.cmpStartPrice.ctrls.iValue.el.addEventListener("input",()=>{
            this._refreshStartPriceLines();
            this._scheduleStartValidation();
        });

        ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount0.ctrls.iValue.el.addEventListener("input",()=>{
            this._scheduleStartValidation();
        });

        ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount1.ctrls.iValue.el.addEventListener("input",()=>{
            this._scheduleStartValidation();
        });
    }

    public async refreshAsync(pool:LPXInfo)
    {
        const ctrl = this.ctrls;
        this._clearStartValidationTimer();
        this._checkCache = undefined;
        this._reserveStatusText = "";
        this._pool = pool;
        const lpxInf=App.Inst.getInst(LPXInfrastructure);
        this._lpxInf=lpxInf;

        const pack = lpxInf.unpackConfigPack(BiMath.WEIToBI(this._pool.packRaw));
        this._ntz = parseFloat(BiMath.ToEth(pack.ntzIndex * BiMath.Decimal / BiMath.Decimal17B));
        this._fundDecimal = BiMath.Pow10(this._pool.fundToken.decimals);
        this._anchorDecimal = BiMath.Pow10(this._pool.anchorToken.decimals);

        const fundAdr = this._pool.fundToken.erc20Address;
        const anchorAdr = this._pool.anchorToken.erc20Address;
        const fundSymbol = this._pool.fundToken.symbol;
        const anchorSymbol = this._pool.anchorToken.symbol;

        ctrl.cmpStartMaxPrice.setToken(anchorSymbol, anchorAdr);
        ctrl.cmpStartPrice.setToken(anchorSymbol, anchorAdr);
        ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount0.setToken(fundSymbol, fundAdr);
        ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount1.setToken(anchorSymbol, anchorAdr);

        ctrl.cmpStartMaxPrice.ctrls.iValue.value=
        ctrl.cmpStartPrice.ctrls.iValue.value=
        ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount0.ctrls.iValue.value=
        ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount1.ctrls.iValue.value="";
        ctrl.btnStartLPX.disable();
        ctrl.cmpChart.setLinePrice("minBuy", 0);
        this._setStartStatus("");

        ctrl.cmpChart.loadLPXAsync(pool);
    }

    private async _tryStartLPXAsync(checkInput:StartPoolCheckInput, checkResult:StartPoolCheckResult)
    {
        const fundIsWPLS = checkInput.fundAddress.toLowerCase() == LPXInfrastructure.ADDRESS_WPLS.toLowerCase();
        const anchorIsWPLS = checkInput.anchorAddress.toLowerCase() == LPXInfrastructure.ADDRESS_WPLS.toLowerCase();
        const erc20Fund = new ERC20Contract(checkInput.fundAddress, App.chain.provider);
        const erc20Anchor = new ERC20Contract(checkInput.anchorAddress, App.chain.provider);

        const balanceEth = await App.chain.getBalanceEth();
        if (balanceEth === undefined)
        {
            App.gui.dialogOkShowAsync("Unable to read wallet balance. Please reconnect wallet and try again.");
            return;
        }
        let valuePayBi=BiMath.Zero;

        if (!fundIsWPLS)
        {
            const balanceFund = await erc20Fund.balanceOf(App.chain.account);
            if (BiMath.ToBI(balanceFund)<checkInput.fundReserve)
            {
                App.gui.dialogOkShowAsync("Insufficient funds");
                return;
            }
        } else
        {
            if (BiMath.ToBI(balanceEth)<checkInput.fundReserve)
            {
                App.gui.dialogOkShowAsync("Insufficient funds");
                return;
            }
            valuePayBi = checkInput.fundReserve;
        }

        if (!anchorIsWPLS)
        {
            const balanceAnchor = await erc20Anchor.balanceOf(App.chain.account);
            if (BiMath.ToBI(balanceAnchor)<checkInput.anchorReserve)
            {
                App.gui.dialogOkShowAsync("Insufficient anchor");
                return;
            }
        } else
        {
            if (BiMath.ToBI(balanceEth)<checkInput.anchorReserve)
            {
                App.gui.dialogOkShowAsync("Insufficient anchor");
                return;
            }
            valuePayBi += checkInput.anchorReserve;
        }
        if (BiMath.ToBI(balanceEth)<valuePayBi)
        {
            App.gui.dialogOkShowAsync("Insufficient funds");
            return;
        }

        const stepTx0 = App.gui.getTxLockText();
        const stepTx1 = 
        "Step 1 of 3\n"+
        "Fund token approval is pending.\n"+
        "Please wait - this may take a few minutes.\n"+
        "Two more transactions will start automatically.\n"
        const stepTx2 = 
        "Step 2 of 3\n"+
        "Anchor token approval is pending.\n"+
        "Please wait - this may take a few minutes.\n"+
        "One more transaction will start automatically.\n"
        const stepTx3 = 
        "Step 3 of 3\n"+
        "LPX is starting.\n"+
        "Please wait - this may take a few minutes.\n"

        try
        {
            if (!fundIsWPLS)
            {
                App.gui.setTxLockText(stepTx1);
                const approved = await erc20Fund.allowance(App.chain.account, this._lpxInf.lpx.address);
                if (BiMath.ToBI(approved)<checkInput.fundReserve)
                {
                    const approveResult = await erc20Fund.approve(this._lpxInf.lpx.address, BiMath.ToEth(checkInput.fundReserve));
                    if (!approveResult) return;
                }
            }
            if (!anchorIsWPLS)
            {
                App.gui.setTxLockText(stepTx2);
                const approved = await erc20Anchor.allowance(App.chain.account, this._lpxInf.lpx.address);
                if (BiMath.ToBI(approved)<checkInput.anchorReserve)
                {
                    const approveResult = await erc20Anchor.approve(this._lpxInf.lpx.address, BiMath.ToEth(checkInput.anchorReserve));
                    if (!approveResult) return;
                }
            }
            App.gui.setTxLockText(stepTx3);
            const result = await this._lpxInf.lpxCtrlPools.poolStart(BiMath.ToEth(valuePayBi), checkInput.poolId, checkResult.reserves);
            if (result) App.gui.dialogOkShowAsync("Transaction successful!","");
            this._lpxInf.refreshAsync();
        }
        finally
        {
            App.gui.setTxLockText(stepTx0);
        }
    }

    private _refreshStartValidation()
    {
        const ctrl = this.ctrls;
        ctrl.btnStartLPX.disable();
        ctrl.cmpChart.setLinePrice("minBuy", 0);
        if (!this._pool || !this._lpxInf) return false;

        const validation = this._getStartValidation();
        if (validation.state == "empty")
        {
            this._setStartStatus("");
            return false;
        }

        if (validation.state == "invalid")
        {
            this._setStartStatus(validation.statusText, "invalid");
            return false;
        }

        ctrl.btnStartLPX.enable();
        const publishedPrices = this._getPublishedPrices(validation.result.reserves, validation.input.feeIndex);
        const minBuyPriceRaw = this._lpxInf.calcStartRatio(validation.result.reserves, validation.input.feeIndex)
            *(BiMath.Decimal17B-validation.input.ntzIndex)/BiMath.Decimal17B;
        ctrl.cmpChart.setLinePrice("minBuy", parseFloat(BiMath.ToEth(minBuyPriceRaw*this._fundDecimal/this._anchorDecimal)));
        const statusText =
            "Published Max Sell Price: "+BiMath.ToEth(publishedPrices.maxSellPrice)+"\n"+
            "Published Sell Price: "+BiMath.ToEth(publishedPrices.sellPrice);
        const reserveStatusText =
            "Fund Reserve: "+this._getTokenTextFromRaw(validation.result.reserves.fundReserve, this._fundDecimal)+" "+this._pool.fundToken.symbol+" (raw "+validation.result.reserves.fundReserve+")\n"+
            "Anchor Reserve: "+this._getTokenTextFromRaw(validation.result.reserves.anchorReserve, this._anchorDecimal)+" "+this._pool.anchorToken.symbol+" (raw "+validation.result.reserves.anchorReserve+")\n"+
            "Fund Img Reserve: "+this._getTokenTextFromRaw(validation.result.reserves.fundImgReserve, this._fundDecimal)+" "+this._pool.fundToken.symbol+" (raw "+validation.result.reserves.fundImgReserve+")\n"+
            "Anchor Img Reserve: "+this._getTokenTextFromRaw(validation.result.reserves.anchorImgReserve, this._anchorDecimal)+" "+this._pool.anchorToken.symbol+" (raw "+validation.result.reserves.anchorImgReserve+")";
        this._setStartStatus(statusText, "", reserveStatusText);
        return true;
    }

    private _getStartValidation():StartPoolValidation
    {
        if (!this._pool || !this._lpxInf) return { state:"empty" };
        const invalid = (statusText:string, dialogText=statusText):StartPoolValidation => ({ state:"invalid", statusText, dialogText });
        const ctrl = this.ctrls;
        const fundInput = ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount0.ctrls.iValue.value;
        const anchorInput = ctrl.cmpStartTokens.ctrls.cmpInputTokenAmount1.ctrls.iValue.value;
        const sellInput = ctrl.cmpStartPrice.ctrls.iValue.value;
        const maxSellInput = ctrl.cmpStartMaxPrice.ctrls.iValue.value;
        const hasAnyInput = !!(maxSellInput.trim() || sellInput.trim() || fundInput.trim() || anchorInput.trim());
        if (!hasAnyInput) return { state:"empty" };

        let checkInput:StartPoolCheckInput;
        try
        {
            const fundReserve = this._getTokenValueRaw(fundInput, this._fundDecimal);
            const anchorReserve = this._getTokenValueRaw(anchorInput, this._anchorDecimal);
            const sellPriceExact = this._getPriceInputBI(sellInput);
            const maxSellPriceExact = this._getPriceInputBI(maxSellInput);
            const sellPriceRaw = sellPriceExact*this._anchorDecimal/this._fundDecimal;
            const maxSellPriceRaw = maxSellPriceExact*this._anchorDecimal/this._fundDecimal;
            const sellPriceRatioNumerator = sellPriceExact*this._anchorDecimal;
            const sellPriceRatioDenominator = BiMath.Decimal*this._fundDecimal;
            const pack = this._lpxInf.unpackConfigPack(BiMath.WEIToBI(this._pool.packRaw));
            checkInput = {
                poolId: this._pool.poolId,
                fundAddress: this._pool.fundToken.erc20Address,
                anchorAddress: this._pool.anchorToken.erc20Address,
                fundReserve,
                anchorReserve,
                sellPriceRaw,
                maxSellPriceRaw,
                sellPriceExact,
                maxSellPriceExact,
                sellPriceExactRatio: {
                    numerator: sellPriceRatioNumerator,
                    denominator: sellPriceRatioDenominator
                },
                feeIndex: pack.feeIndex,
                ntzIndex: pack.ntzIndex,
                key: [
                    this._pool.poolId,
                    this._pool.fundToken.erc20Address,
                    this._pool.anchorToken.erc20Address,
                    fundReserve,
                    anchorReserve,
                    sellPriceRaw,
                    maxSellPriceRaw,
                    maxSellPriceExact,
                    sellPriceRatioNumerator,
                    sellPriceRatioDenominator,
                    pack.feeIndex,
                    pack.ntzIndex
                ].join("|")
            };
        }
        catch (error)
        {
            return invalid("Invalid numeric input.");
        }

        if (checkInput.fundReserve < 0n)
            return invalid("Start Fund Amount cannot be negative.");
        if (checkInput.anchorReserve < 0n)
            return invalid("Start Anchor Amount cannot be negative.");
        if (checkInput.sellPriceExact < 0n)
            return invalid("Sell Price cannot be negative.");
        if (checkInput.maxSellPriceExact < 0n)
            return invalid("Max Sell Price cannot be negative.");

        if ((sellInput.trim() && checkInput.sellPriceRaw == 0n) || (maxSellInput.trim() && checkInput.maxSellPriceRaw == 0n))
            return invalid("These prices are below the minimum tick for this token pair. Increase Sell Price or Max Sell Price.");

        if (!(checkInput.fundReserve > 0n && checkInput.sellPriceRaw > 0n && checkInput.maxSellPriceRaw > 0n))
            return invalid("Enter Start Fund Amount, Sell Price, and Max Sell Price to calculate published prices.");

        const maxSellPriceEffective = checkInput.maxSellPriceRaw*this._fundDecimal/this._anchorDecimal;
        if (checkInput.sellPriceExact > maxSellPriceEffective)
            return invalid(
                "Sell Price is above the effective Max Sell Price. Increase Max Sell Price or lower Sell Price.",
                "Sell Price should not be above effective Max Sell Price."
            );

        let checkResult:StartPoolCheckResult;
        if (this._checkCache && this._checkCache.key == checkInput.key) checkResult = this._checkCache.result;
        else
        {
            checkResult = this._lpxInf.check(
                checkInput.fundReserve,
                checkInput.anchorReserve,
                checkInput.sellPriceRaw,
                checkInput.maxSellPriceRaw,
                checkInput.feeIndex,
                checkInput.sellPriceExactRatio.numerator,
                checkInput.sellPriceExactRatio.denominator
            );
            this._checkCache = {key:checkInput.key, result:checkResult};
        }

        if (checkResult.error)
            return invalid(
                "These prices cannot be published with the current token amount. Adjust Sell Price, Max Sell Price, or Start Fund Amount.",
                "Can't start LPX with these parameters. Need to change one or more of the input parameters: Max Price, Sell Price or Start Amount.\n\n"+checkResult.error
            );

        return { state:"valid", input:checkInput, result:checkResult };
    }

    private _scheduleStartValidation()
    {
        this._checkCache = undefined;
        this.ctrls.btnStartLPX.disable();
        this.ctrls.cmpChart.setLinePrice("minBuy", 0);
        this._setStartStatus("");
        this._clearStartValidationTimer();
        this._validationTimer = window.setTimeout(()=>{
            this._validationTimer = undefined;
            this._refreshStartValidation();
        }, CmpWidgetLPXCtrlTabStartPool.VALIDATION_DEBOUNCE_MS);
    }

    private _clearStartValidationTimer()
    {
        if (this._validationTimer === undefined) return;
        window.clearTimeout(this._validationTimer);
        this._validationTimer = undefined;
    }

    private _getPublishedPrices(reserves:LPXReserves, feeIndex:bigint)
    {
        const fundTotal = reserves.fundReserve + reserves.fundImgReserve;
        const anchorTotal = reserves.anchorReserve + reserves.anchorImgReserve;
        const sellPrice = anchorTotal*this._fundDecimal*BiMath.Decimal/(fundTotal*this._anchorDecimal);
        const maxSellPriceRaw = this._lpxInf.calcEndRatio(reserves, feeIndex);
        const maxSellPrice = maxSellPriceRaw*this._fundDecimal/this._anchorDecimal;
        return { sellPrice, maxSellPrice };
    }

    private _refreshStartPriceLines()
    {
        const ctrl = this.ctrls;
        const maxSell = this._getInputNumber(ctrl.cmpStartMaxPrice.ctrls.iValue);
        const sellPrice = this._getInputNumber(ctrl.cmpStartPrice.ctrls.iValue);
        ctrl.cmpChart.setLinePrice("maxSell", maxSell);
        ctrl.cmpChart.setLinePrice("sell", sellPrice);
        if (!sellPrice)
        {
            ctrl.cmpChart.setLinePrice("buy", 0);
            return;
        }
        ctrl.cmpChart.setLinePrice("buy", sellPrice*(1-this._ntz));
    }

    private _getInputNumber(input:gs.InputGS)
    {
        const value = parseFloat(input.value.trim());
        if (!Number.isFinite(value) || value <= 0) return 0;
        return value;
    }

    private _getTokenValueRaw(value:string, decimal:bigint)
    {
        value = value.trim();
        if (!value) return BiMath.Zero;
        return BiMath.ToBI(value)*decimal/BiMath.Decimal;
    }

    private _getPriceInputBI(value:string)
    {
        value = value.trim();
        if (!value) return BiMath.Zero;
        return BiMath.ToBI(value);
    }

    private _getTokenTextFromRaw(rawValue:bigint, decimal:bigint)
    {
        return BiMath.ToEth(rawValue*BiMath.Decimal/decimal);
    }

    private _setStartStatus(text:string, state:StartPoolStatusState="", reserveStatusText:string="")
    {
        this._reserveStatusText = reserveStatusText;
        if (!this._status) return;
        this._status.text = text;
        this._status.removeClass("warning");
        this._status.removeClass("invalid");
        if (state) this._status.addClass(state);
        this._status.el.title = reserveStatusText ? "Click to view published reserves" : "";
        this._status.el.style.cursor = reserveStatusText ? "pointer" : "";
    }
}
